"use client";

import { SidebarLeft } from "@/src/pages/study/components/layout/sidebar-left";
import { SidebarRight } from "@/src/pages/study/components/layout/sidebar-right";
import { SidebarInset, SidebarProvider } from "@/components/ui/sidebar";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  getStudyNotebooksByIdDocuments,
  Notebook,
  postStudyNotebooksByIdDocuments,
} from "../client";
import { useCommunication } from "@/lib/CommunicationWrapper";
import {
  StudyDocument,
  StudyUploadedDocument,
  StudyNotUploadedDocument,
  UploadableSource,
  isURLSource,
  isExternalSource,
  UploadableURL,
} from "../types";
import { CanvasCourse } from "@/lib/types";
import { ChatInterface } from "./layout/chat-interface";
import { makeModelAdapter } from "../hooks/useChatRuntime";
import { AssistantRuntimeProvider, useLocalRuntime } from "@assistant-ui/react";
import { useNotebookContext } from "../hooks/useNotebook";
import { toast } from "sonner";
import { AddSourcesModal } from "./layout/add-sources-modal";
import { HighlightsInterface } from "./layout/highlights/highlight-interface";
import { cn } from "@/lib/utils";
import { OneClickGenerateModal } from "./layout/study-sets/one-click-modal";
import { CreateNotebookModal } from "./layout/create-notebook-modal";
import { SourceInterface } from "./layout/highlights/source-interface";
import {
  ResizablePanelGroup,
  ResizablePanel,
  ResizableHandle,
} from "@/components/ui/resizable";
import { AddSourcesModalB } from "./layout/add-sources-modal-b";
import { useAuth } from "@/lib/auth";
import { getABBucket } from "@/lib/ab";

interface StudyWorkspaceProps {
  courses: CanvasCourse[];
  createNotebook: (data: { name: string; course: CanvasCourse }) => void;
}

export function StudyWorkspace({
  courses,
  createNotebook,
}: StudyWorkspaceProps) {
  const {
    notebook: selectedNotebook,
    notebooks,
    setNotebook: onNotebookChange,
    sources,
    highlights,
    addSource,
    selectedSources,
    editNotebook,
    createFlowStep,
    setCreateFlowStep: setInCreateFlow,
    currentPage,
    removeSource,
    editSource,
    navigatePage,
    addSelectedSource,
    removeSelectedSource,
  } = useNotebookContext();

  const { sendMessageAndListen, openGate } = useCommunication();
  const { abBucket } = useAuth();

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const documentId = urlParams.get("document_id");
    const highlightId = urlParams.get("highlight_id");
    if (documentId && !highlightId) {
      const doc = sources.find((s) => s.id.toString() === documentId);
      if (doc) navigatePage({ type: "source", value: doc });
    }
    if (documentId && highlightId) {
      const highlight = highlights.find((h) => h.id.toString() === highlightId);
      const doc = sources.find((s) => s.id.toString() === documentId);
      if (highlight && doc)
        navigatePage({
          type: "source_highlight",
          highlight: highlight,
          source: doc,
        });
    }
  }, []);

  async function uploadExternalSource(file: File) {
    const loadingToastId = toast.loading("Processing uploaded file…", {
      description: <span className="text-theme-1">{file.name}</span>,
    });

    const result = await postStudyNotebooksByIdDocuments({
      body: {
        file,
        name: file.name,
        source: "user_upload",
        visibility: "private",
        notebook_id: selectedNotebook.id,
        metadata: JSON.stringify({
          size: file.size,
          content_type: file.type,
        }),
      },
      path: { id: selectedNotebook.id.toString() },
    });

    toast.dismiss(loadingToastId);

    if (result.error) {
      if (result.response.status === 402) {
        openGate("study_files");
      } else {
        toast.error(`Error processing ${file.name}`, {
          description: (
            <span className="text-theme-1">{JSON.stringify(result.error)}</span>
          ),
        });
      }
      return;
    }

    toast.success("File added", {
      description: <span className="text-theme-1">{file.name}</span>,
    });
    addSource(result.data);
  }

  async function uploadLink(url: string) {
    const loadingToastId = toast.loading("Processing uploaded URL…", {
      description: <span className="text-theme-1">{url}</span>,
    });

    let result = await sendMessageAndListen({
      type: "CALL_EXTENSION_FUNC",
      payload: {
        func: "uploadStudyDocumentLink",
        args: { url: url, notebook: selectedNotebook.id },
      },
    });

    // backwards compatibility
    if (result.error && result.error === "Unknown function") {
      result = await postStudyNotebooksByIdDocuments({
        body: {
          url,
          name: url,
          source: "user_upload",
          visibility: "private",
          notebook_id: selectedNotebook.id,
          metadata: JSON.stringify({
            size: url.length,
            content_type: "text/html",
            url: url,
          }),
        },
        path: { id: selectedNotebook.id.toString() },
      });
    }

    toast.dismiss(loadingToastId);

    if (result.error) {
      if ("response" in result && result.response.status === 402) {
        openGate("study_files");
      } else {
        toast.error(`Error processing ${url}`, {
          description: (
            <span className="text-theme-1">{JSON.stringify(result.error)}</span>
          ),
        });
      }
      return;
    }

    toast.success("File added", {
      description: <span className="text-theme-1">{url}</span>,
    });
    addSource(result.data);
  }

  async function uploadCourseDocument(document: StudyNotUploadedDocument) {
    const loadingToastId = toast.loading("Processing uploaded file…", {
      description: <span className="text-theme-1">{document.name}</span>,
    });
    const result = await sendMessageAndListen({
      type: "CALL_EXTENSION_FUNC",
      payload: {
        func: "uploadStudyDocument",
        args: { document: document, notebook: selectedNotebook.id },
      },
    });

    toast.dismiss(loadingToastId);

    if (!result.data || !result.success) {
      if (result.status === 402) {
        openGate("study_files");
      } else {
        toast.error(`Failed to process course document.`, {
          description: (
            <span className="text-theme-1">{JSON.stringify(result.error)}</span>
          ),
        });
      }
      return;
    }

    toast.success("File added", {
      description: <span className="text-theme-1">{document.name}</span>,
    });
    addSource(result.data);
  }

  async function uploadSource(source: UploadableSource) {
    if (isURLSource(source))
      return await uploadLink((source as UploadableURL).url);
    else if (isExternalSource(source))
      return await uploadExternalSource(source as File);
    else return await uploadCourseDocument(source as StudyNotUploadedDocument);
  }

  const handleSourceToggle = (selectedDocument: StudyDocument) => {
    if (selectedSources.includes(selectedDocument)) {
      removeSelectedSource(selectedDocument);
    } else {
      addSelectedSource(selectedDocument);
    }
  };

  function renameNotebook(name: string) {
    editNotebook({
      ...selectedNotebook,
      name: name,
    });
  }

  const makeAdapter = useCallback(() => {
    if (!selectedNotebook) return makeModelAdapter("undefined_notebook", []);
    return makeModelAdapter(selectedNotebook.id.toString(), selectedSources);
  }, [selectedNotebook, selectedSources]);

  const runtime = useLocalRuntime(makeAdapter(), {});

  const onNotebookChangeClearThread = useCallback(
    (notebook: Notebook) => {
      runtime.thread.reset();
      onNotebookChange(notebook);
    },
    [runtime]
  );

  const onCreateNotebookClearThread = useCallback(
    (data: { name: string; course: CanvasCourse }) => {
      runtime.thread.reset();
      createNotebook(data);
    },
    [runtime]
  );

  const showCourseModal = createFlowStep === "course";
  const allowCloseCourseModal = selectedNotebook && showCourseModal;
  const showUploadModal =
    createFlowStep === "files" || createFlowStep === "files_init";
  const onCloseUploadModal = () => {
    setInCreateFlow("done");
  };

  const isSourceView =
    currentPage.type === "source" ||
    currentPage.type === "source_highlight" ||
    currentPage.type === "source_highlight_category";

  const isChatView =
    currentPage.type === "source" || currentPage.type === "chat";

  const isHighlightView =
    currentPage.type === "highlight" ||
    currentPage.type === "highlight_category" ||
    currentPage.type === "source_highlight" ||
    currentPage.type === "source_highlight_category";

  const ab = abBucket.upload_source_modal_v2 < 50 ? 0 : 1;
  const SourceModal = [AddSourcesModal, AddSourcesModalB][ab];

  return (
    <div className="h-full flex">
      <SidebarProvider className="h-full">
        <AssistantRuntimeProvider runtime={runtime}>
          <SidebarProvider>
            <SidebarLeft
              sources={sources}
              selectedSources={selectedSources}
              onSourceToggle={handleSourceToggle}
              openUploadModal={() => setInCreateFlow("files")}
              courses={courses}
              notebooks={notebooks}
              openedSource={
                currentPage.type === "source"
                  ? currentPage.value
                  : currentPage.type === "source_highlight"
                  ? currentPage.source
                  : currentPage.type === "source_highlight_category"
                  ? currentPage.source
                  : null
              }
              selectedNotebook={selectedNotebook}
              onNotebookChange={onNotebookChangeClearThread}
              openLanding={() => setInCreateFlow("landing")}
              enterCreateFlow={() => setInCreateFlow("course")}
              onSourceOpen={(source: StudyDocument) => {
                addSelectedSource(source);
                navigatePage({ type: "source", value: source });
              }}
              onSourceRemove={removeSource}
              onSourceRename={editSource}
            />

            <div className="relative h-full w-full bg-theme-0">
              {
                <ResizablePanelGroup>
                  {/* Source Viewer - Left Side */}
                  {isSourceView && (
                    <>
                      <ResizablePanel defaultSize={50} minSize={30}>
                        <div className="h-full">
                          <SourceInterface />
                        </div>
                      </ResizablePanel>

                      <ResizableHandle withHandle />
                    </>
                  )}

                  {/* Chat Interface - Right Side */}
                  {isChatView && (
                    <ResizablePanel defaultSize={20} minSize={20}>
                      {/* Chat */}
                      <div
                        className={cn(
                          !isSourceView &&
                            "absolute inset-0 transition-opacity duration-200",
                          "opacity-100 h-full"
                        )}
                      >
                        <ChatInterface
                          notebookName={
                            selectedNotebook?.name || "Untitled Study Set"
                          }
                          selectedSourcesCount={selectedSources.length}
                          totalSourcesCount={sources.length}
                          runtime={runtime}
                          onRenameNotebook={renameNotebook}
                          showSidebarTrigger={!isSourceView}
                        />
                      </div>
                    </ResizablePanel>
                  )}

                  {isHighlightView && (
                    <ResizablePanel
                      defaultSize={80}
                      minSize={50}
                      className="bc-resizable-panel-container"
                    >
                      {/* Highlights */}
                      <div
                        className={cn(
                          !isSourceView &&
                            "absolute inset-0 transition-opacity duration-200 h-full",
                          "opacity-100 h-full"
                        )}
                      >
                        <HighlightsInterface />
                      </div>
                    </ResizablePanel>
                  )}
                </ResizablePanelGroup>
              }
            </div>
          </SidebarProvider>

          <SidebarRight />
        </AssistantRuntimeProvider>
      </SidebarProvider>
      <CreateNotebookModal
        courses={courses}
        open={showCourseModal}
        allowClickAway={allowCloseCourseModal}
        onConfirm={onCreateNotebookClearThread}
        onCancel={() =>
          setInCreateFlow(allowCloseCourseModal ? "done" : "landing")
        }
      />

      {selectedNotebook && (
        <SourceModal
          open={showUploadModal}
          onClose={onCloseUploadModal}
          courses={courses}
          uploadSource={uploadSource}
          init={createFlowStep === "files_init"}
        />
      )}
    </div>
  );
}
